import { useEffect, useState, useRef, useMemo } from "react";
import { fetchBatchImagesByBookId } from "../../../../services/mediaService";
import Button from "../../../forms/Button";
import CropperModal from "./DNDCROP/CropperModal";
import { arrayMove } from "@dnd-kit/sortable";
import { DndContext, useSensor, useSensors, PointerSensor, closestCenter } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import DraggableImageItem from "./DNDCROP/DraggableSketchImageItem";
import useFetch from "../../../../hooks/useFetch";
import { saveStep } from "../../../../services/stepService";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { processSketchJob } from "./processSketchJob";
import { multipleMediaAdd } from "../../../../services/mediaService";
import useMutation from "../../../../hooks/useMutation";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { reorderBooks } from "../../../../services/bookService";
import { setCoverImages, setImages as setReduxImages } from "../../../../redux/slice/sketchSlice";
import { useDispatch } from "react-redux";
import StepProgressView from "../../../common/StepProgressView";
import { createShopifyCheckout } from "../../../../services/shopifyService";
import { getActiveStep } from "../../../../services/commonService";
import { markStepAsVisited, updateBookOrderDate } from "../../../../redux/slice/profileSlice";
import Image from "../../../elements/Image";
import { resetStep } from "../../../../redux/slice/stepSlice";
import { io } from "socket.io-client";
import { SOCKET_URL } from "../../../../config/apiConfig";

// 🔧 FIXED: Enhanced Upload Progress Notification Component with real Socket.IO data
const UploadProgressNotification = ({ progressData }) => {
  const [animationProgress, setAnimationProgress] = useState(0);
  const { uploadingImages = [] } = useSelector(state => state.stepFlow);
  
  const { 
    currentImage = 0, 
    totalImages = 0, 
    overallPercentage = 0, 
    currentStage = '', 
    isProcessing = false 
  } = progressData || {};

  // 🆕 Show loader if uploadingImages has items OR if processing with socket data
  const shouldShowLoader = uploadingImages.length > 0 || isProcessing;
  
  // 🆕 Determine if we have actual progress data from socket
  const hasSocketData = isProcessing && (overallPercentage > 0 || currentStage);

  // Smooth animation to match backend progress
  useEffect(() => {
    if (!shouldShowLoader) {
      setAnimationProgress(0);
      return;
    }

    // If only uploadingImages exist (no socket data yet), stay at 0%
    if (uploadingImages.length > 0 && !hasSocketData) {
      setAnimationProgress(0);
      return;
    }

    // Set immediate progress update from socket
    const targetPercentage = overallPercentage || 0;
    
    // Smooth transition to target
    const interval = setInterval(() => {
      setAnimationProgress((current) => {
        const diff = targetPercentage - current;
        if (Math.abs(diff) < 1) {
          return targetPercentage;
        }
        return current + diff * 0.2; // Smooth interpolation
      });
    }, 100);

    return () => clearInterval(interval);
  }, [overallPercentage, shouldShowLoader, uploadingImages.length, hasSocketData]);

  // Format stage name for display
  const formatStage = (stage) => {
    if (!stage) return 'Preparing';
    const stages = {
      'downloading': 'Downloading',
      'inferencing': 'Processing',
      'uploading': 'Finalizing'
    };
    return stages[stage.toLowerCase()] || 'Processing';
  };

  // Get status message
  const getStatusMessage = () => {
    // 🆕 If we have uploadingImages but no socket data yet
    if (uploadingImages.length > 0 && !hasSocketData) {
      return "🚀 Starting the magic... Your images are being queued for processing!";
    }

    if (!shouldShowLoader) {
      return "🎉 All done! Your coloring book is ready to review.";
    }
    
    if (currentStage === 'downloading') {
      return "📥 Downloading your images...";
    } else if (currentStage === 'inferencing') {
      return "✨ Transforming your photos into beautiful coloring pages...";
    } else if (currentStage === 'uploading') {
      return "☁️ Saving your masterpieces...";
    }
    
    return "✨ Transforming your photos into beautiful coloring pages...";
  };

  // Get secondary message
  const getSecondaryMessage = () => {
    // 🆕 If we have uploadingImages but no socket data yet
    if (uploadingImages.length > 0 && !hasSocketData) {
      return `Preparing ${uploadingImages.length} image${uploadingImages.length > 1 ? 's' : ''} for transformation...`;
    }

    if (!shouldShowLoader) {
      return "Thank you for your patience!";
    }
    
    if (currentImage && totalImages) {
      return `Processing image ${currentImage} of ${totalImages}`;
    }
    
    return "This may take a few moments—we're making every detail perfect!";
  };

  // 🆕 Don't render if there's nothing to show
  if (!shouldShowLoader) {
    return null;
  }

  return (
    <div className="mb-8 p-8 bg-white border-2 border-md-pink rounded-3xl shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-center space-x-3 mb-6">
        <div className="w-12 h-12 bg-md-pink rounded-full flex items-center justify-center animate-pulse">
          <i className="fa-solid fa-wand-magic-sparkles text-white text-lg"></i>
        </div>
        <h3 className="text-gray-900 font-bold text-2xl">Creating Your Masterpiece</h3>
      </div>

      {/* Progress Bar Container */}
      <div className="space-y-4">
        {/* Progress Text */}
        <div className="flex justify-between items-center text-base">
          <span className="text-gray-700 font-semibold">
            {currentImage && totalImages 
              ? `${formatStage(currentStage)}: ${currentImage}/${totalImages}` 
              : formatStage(currentStage)}
          </span>
          <span className="text-md-pink font-bold text-xl">
            {Math.round(animationProgress)}%
          </span>
        </div>

        {/* Progress Bar */}
        <div className="relative">
          <div className="w-full h-4 bg-white rounded-full shadow-inner border-2 border-pink-200">
            <div 
              className="h-full bg-md-pink rounded-full transition-all duration-500 ease-out relative overflow-hidden" 
              style={{ width: `${animationProgress}%` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
            </div>
          </div>

          {/* Progress indicator dot */}
          <div
            className="absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-md-pink rounded-full shadow-xl border-3 border-white transition-all duration-500 ease-out"
            style={{
              left: `calc(${animationProgress}% - 10px)`,
              opacity: animationProgress > 5 ? 1 : 0,
            }}
          >
            <div className="absolute inset-1 bg-white rounded-full opacity-40 animate-ping"></div>
          </div>
        </div>

        {/* Status Message */}
        <div className="text-center bg-white/60 rounded-2xl p-4 backdrop-blur-sm">
          <p className="text-sm text-gray-700 leading-relaxed font-medium">
            {getStatusMessage()}
          </p>
          <p className="text-xs text-gray-600 mt-1">
            {getSecondaryMessage()}
          </p>
        </div>

        {/* Decorative elements */}
        <div className="flex justify-center space-x-2 pt-2">
          <div className="w-2.5 h-2.5 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: "0s" }}></div>
          <div className="w-2.5 h-2.5 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: "0.15s" }}></div>
          <div className="w-2.5 h-2.5 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: "0.3s" }}></div>
        </div>
      </div>
    </div>
  );
};

const MAX_REFETCH_ATTEMPTS = 30;
const REFETCH_INTERVAL = 20000;
const SYNC_CHECK_INTERVAL = 5000;

// Utility function to apply crop coordinates to an image URL
const applyCropToImage = async (imageUrl, cropData, outputFileName = null) => {
  return new Promise((resolve, reject) => {
    try {
      if (!cropData) {
        fetch(imageUrl)
          .then((response) => response.blob())
          .then((blob) => {
            const file = new File([blob], outputFileName || `image-${Date.now()}.jpg`, {
              type: blob.type || "image/jpeg",
              lastModified: Date.now(),
            });
            resolve(file);
          })
          .catch(reject);
        return;
      }
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const imgWidth = img.naturalWidth;
        const imgHeight = img.naturalHeight;
        let { x, y, width, height } = cropData;
        x = Math.max(0, Math.min(x, imgWidth - 1));
        y = Math.max(0, Math.min(y, imgHeight - 1));
        width = Math.max(1, Math.min(width, imgWidth - x));
        height = Math.max(1, Math.min(height, imgHeight - y));
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, x, y, width, height, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Failed to create cropped image blob"));
              return;
            }
            const file = new File([blob], outputFileName || `cropped-${Date.now()}.jpg`, {
              type: "image/jpeg",
              lastModified: Date.now(),
            });
            resolve(file);
          },
          "image/jpeg",
          0.9
        );
      };
      img.onerror = () => {
        reject(new Error("Failed to load image for cropping"));
      };
      img.src = imageUrl;
    } catch (error) {
      reject(error);
    }
  });
};

const ReviewBookContent = () => {
  const [croppingImage, setCroppingImage] = useState(null);
  const [selectedForRegen, setSelectedForRegen] = useState([]);
  const [images, setImages] = useState([]);
  const [, setRebatch] = useState(0);
  const [, setSyncAttempts] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regeneratingImages, setRegeneratingImages] = useState(new Set());
  
  // 🆕 NEW: Local state for lineart progress
  const [lineartProgress, setLineartProgress] = useState({
    isProcessing: false,
    currentImage: 0,
    totalImages: 0,
    overallPercentage: 0,
    currentStage: '',
  });
  
  const { activeBookId, forceRefetch, uploadingImages, uploadingNewImages } = useSelector((state) => state.stepFlow);
  const { images: sketchImages, coverImages, batchId } = useSelector((state) => state.sketch);
  const myBooks = useSelector(state => state.profile.myBooks);
  const { session } = useSelector((state) => state.authProvider);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const syncTimeoutRef = useRef(null);
  const checkIntervalRef = useRef(null);
  const isMountedRef = useRef(true);
  const prevUploadingImagesLengthRef = useRef(null);
  
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);

  const RUNPOD_API_KEY = process.env.REACT_APP_RUNPOD_API_KEY;
  const RUNPOD_ID = process.env.REACT_APP_RUNPOD_ID;
  const BASE_URL = `https://api.runpod.ai/v2/${RUNPOD_ID}`;
  const MAX_POLL_LIMIT = 100;

  const { mutate: uploadToBackend } = useMutation({
    mutationFn: async (payload) => {
      const res = await multipleMediaAdd(payload);
      return res;
    },
    onSuccess: (res) => {
      if (res?.status === 201) {
        // Success
      } else {
        toast.warning(res.message || "Unexpected response.");
      }
    },
    onError: (err) => {
      console.error("Upload failed:", err);
      toast.error("Upload failed for regenerated image.");
    },
  });

  const sortableItems = useMemo(() => {
    return images
      .filter((img) => !img.isCover)
      .map((img) => img.id ?? img._id);
  }, [images]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, []);

  const {
    data: batchImagesData,
    isLoading: isLoadingBatchImages,
    refetch: refetchBatchImages,
  } = useFetch({
    dependencies: ["batchImages", activeBookId, forceRefetch],
    enabled: !!activeBookId,
    fetchFunction: async () => {
      if (!activeBookId) return null;
      const result = await fetchBatchImagesByBookId(activeBookId);
      setRebatch((cur) => cur + 1);
      return result?.docs?.data || [];
    },
  });

  useEffect(() => {
    if (batchImagesData) {
      const transformed = batchImagesData.map((item, index) => ({
        id: item._id,
        originalUrl: item.url,
        sketchUrl: item.AIUrl,
        isCover: item.isCover,
        regenerateCount: item.regeneratedCount,
        index,
        cropData: null,
        croppedSketchUrl: null,
      }));

      setImages((prevImages) => {
        if (prevImages.length !== transformed.length) {
          return transformed;
        }
        const hasChanges = transformed.some((newImg, idx) => {
          const prevImg = prevImages[idx];
          return !prevImg || prevImg.id !== newImg.id || prevImg.sketchUrl !== newImg.sketchUrl || prevImg.originalUrl !== newImg.originalUrl;
        });

        if (hasChanges) {
          return transformed.map((newImg, idx) => {
            const prevImg = prevImages[idx];
            if (prevImg && prevImg.id === newImg.id) {
              return newImg;
            }
            return newImg;
          });
        }
        return prevImages;
      });
    }
  }, [batchImagesData]);

  useEffect(() => {
    const currentLength = uploadingImages?.length || 0;
    const prevLength = prevUploadingImagesLengthRef.current;

    prevUploadingImagesLengthRef.current = currentLength;

    if (prevLength !== null && currentLength < prevLength) {
      console.log(`📉 uploadingImages decreased: ${prevLength} → ${currentLength}, refetching batch images...`);
      setTimeout(async () => {
        try {
          await refetchBatchImages();
          console.log("✅ Batch images refetched after upload completion");
        } catch (error) {
          console.error("❌ Failed to refetch after upload completion:", error);
        }
      }, 1000);
    }
  }, [uploadingImages?.length, refetchBatchImages]);

  const needsSync = () => {
    const totalReduxImages = (sketchImages?.length || 0) + (coverImages?.length || 0);
    const currentLocalImages = images.length;
    console.log(`🔍 Sync Check: Local=${currentLocalImages}, Redux=${totalReduxImages}`);
    return currentLocalImages !== totalReduxImages;
  };

  const performSyncRefetch = async (attemptNumber) => {
    if (!isMountedRef.current || attemptNumber >= MAX_REFETCH_ATTEMPTS) {
      if (attemptNumber >= MAX_REFETCH_ATTEMPTS) {
        console.log("❌ Max sync attempts reached. Stopping auto-sync.");
      }
      setIsSyncing(false);
      setSyncAttempts(0);
      return;
    }

    try {
      console.log(`🔄 Syncing data... (Attempt ${attemptNumber + 1}/${MAX_REFETCH_ATTEMPTS})`);
      setIsSyncing(true);
      setSyncAttempts(attemptNumber + 1);

      await refetchBatchImages();

      setTimeout(() => {
        if (!isMountedRef.current) return;

        if (needsSync()) {
          console.log(`⏳ Still not synced. Retrying in ${REFETCH_INTERVAL / 1000} seconds...`);
          syncTimeoutRef.current = setTimeout(() => {
            performSyncRefetch(attemptNumber + 1);
          }, REFETCH_INTERVAL);
        } else {
          console.log("✅ Sync successful!");
          setIsSyncing(false);
          setSyncAttempts(0);
        }
      }, 1000);
    } catch (error) {
      console.error("Sync refetch error:", error);
      if (attemptNumber < MAX_REFETCH_ATTEMPTS) {
        console.log(`🔄 Sync error. Retrying in ${REFETCH_INTERVAL / 1000} seconds...`);
        syncTimeoutRef.current = setTimeout(() => {
          performSyncRefetch(attemptNumber + 1);
        }, REFETCH_INTERVAL);
      } else {
        console.log("❌ Max sync attempts reached after errors.");
        setIsSyncing(false);
        setSyncAttempts(0);
      }
    }
  };

  useEffect(() => {
    if (!images.length || isLoadingBatchImages) return;

    checkIntervalRef.current = setInterval(() => {
      if (!isMountedRef.current || isSyncing) return;

      if (needsSync()) {
        console.log("🚨 Sync mismatch detected. Starting auto-sync...");
        performSyncRefetch(0);
      }
    }, SYNC_CHECK_INTERVAL);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [images.length, sketchImages?.length, coverImages?.length, isLoadingBatchImages, isSyncing]);

  const toggleRegenSelect = (id) => {
    setSelectedForRegen((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const fetchImageAsBase64 = async (url) => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const urlToFile = async (url, filename) => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new File([blob], filename, {
      type: blob.type || "image/jpeg",
      lastModified: Date.now(),
    });
  };

  const onCropSave = ({ blob, url, cropData }) => {
    const updated = images.map((img) =>
      img.id === croppingImage.id
        ? {
          ...img,
          croppedSketchUrl: url,
          cropData: cropData,
        }
        : img
    );
    setImages(updated);
    setCroppingImage(null);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) return;

    const activeId = active.id ?? active._id;
    const overId = over.id ?? over._id;

    if (activeId !== overId) {
      const oldIndex = images.findIndex((i) => (i.id ?? i._id) === activeId);
      const newIndex = images.findIndex((i) => (i.id ?? i._id) === overId);

      if (oldIndex !== -1 && newIndex !== -1) {
        const updated = arrayMove(images, oldIndex, newIndex);
        reorderBooks({ ordering_data: updated.map((item, i) => ({ id: item.id, ordering: i })) });
        setImages(updated);

        const orderLookup = {};
        updated.forEach((item, index) => {
          const itemId = item.id ?? item._id;
          orderLookup[itemId] = index;
        });

        const reorderedReduxImages = [...sketchImages].sort((a, b) => {
          const aId = a.id ?? a._id;
          const bId = b.id ?? b._id;
          const aHasUnderscore = !!a._id;
          const bHasUnderscore = !!b._id;

          if (aHasUnderscore && !bHasUnderscore) return -1;
          if (!aHasUnderscore && bHasUnderscore) return 1;

          const aOrder = orderLookup[aId];
          const bOrder = orderLookup[bId];

          if (aOrder !== undefined && bOrder !== undefined) {
            return aOrder - bOrder;
          }

          if (aOrder !== undefined) return -1;
          if (bOrder !== undefined) return 1;

          return 0;
        });

        dispatch(setReduxImages(reorderedReduxImages));
      }
    }
  };

  const handleRegenerate = async () => {
    if (selectedForRegen.length === 0) return;

    setIsRegenerating(true);

    try {
      const imagesToRegenerate = images.filter((img) => selectedForRegen.includes(img.id) && !img.isCover);

      if (imagesToRegenerate.length === 0) {
        toast.error("No valid images selected for regeneration.");
        return;
      }

      console.log(`🔄 Starting regeneration for ${imagesToRegenerate.length} images...`);

      const regeneratingIds = new Set(imagesToRegenerate.map((img) => img.id));
      setRegeneratingImages(regeneratingIds);

      const processableImages = await Promise.all(
        imagesToRegenerate.map(async (img, index) => {
          try {
            const filename = `regenerate-${img.id}-${Date.now()}.jpg`;

            let file;
            if (img.cropData) {
              console.log(`🎯 Applying crop to original image for ${img.id}`);
              file = await applyCropToImage(img.originalUrl, img.cropData, filename);
            } else {
              file = await urlToFile(img.originalUrl, filename);
            }

            return {
              id: img.id,
              isEditable: true,
              regenerateCount: img.regenerateCount,
              file: file,
              url: img.originalUrl,
              cropData: img.cropData,
            };
          } catch (error) {
            console.error(`Failed to process image ${img.id}:`, error);
            toast.error(`Failed to process image for regeneration.`);
            return null;
          }
        })
      );

      const validImages = processableImages.filter((img) => img !== null);

      if (validImages.length === 0) {
        toast.error("Failed to prepare images for regeneration.");
        setRegeneratingImages(new Set());
        return;
      }

      setSelectedForRegen([]);

      await processSketchJob({
        images: validImages,
        uploadToBackend,
        batchId: batchId || uuidv4(),
        BASE_URL,
        RUNPOD_API_KEY,
        MAX_POLL_LIMIT,
        onCoverReady: null,
        bookId: activeBookId,
        shouldRegenerate: true,
        onImageComplete: async (imageIndex, completedImage) => {
          console.log(`🔄 Image ${imageIndex + 1} completed, triggering refetch...`);
          const completedImageId = validImages[imageIndex]?.id;
          if (completedImageId) {
            setRegeneratingImages((prev) => {
              const updated = new Set(prev);
              updated.delete(completedImageId);
              return updated;
            });
          }

          setTimeout(async () => {
            await refetchBatchImages();
            console.log(`✅ Refetch completed for image ${imageIndex + 1}`);
          }, 2000);
        },
        onError: () => {
          setRegeneratingImages(new Set());
          setIsRegenerating(false);
        },
      });

      toast.success(`Regeneration completed for ${validImages.length} images.`);
    } catch (error) {
      console.error("Regeneration error:", error);
      toast.error("Failed to start regeneration process.");
      setRegeneratingImages(new Set());
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleFinalize = async () => {
    const preSelectedPlan = localStorage.getItem("selectedPlan");
    if (!!preSelectedPlan) {
      setSubmitButtonDisabled(true)

      const planObj = JSON.parse(preSelectedPlan);

      try {
        const bookId = activeBookId;
        const bookData = myBooks.find(({ _id }) => activeBookId === _id)

        console.log('📦 Creating checkout for:', {
          plan: planObj.name,
          variantId: planObj.shopifyVariantId,
          bookId,
        });

        const checkoutUrl = await createShopifyCheckout(
          planObj.shopifyVariantId,
          bookId,
          bookData.title,
          bookData.coverImage,
          planObj.name
        );

        console.log('✅ Redirecting to checkout:', checkoutUrl);

        window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
      } catch (error) {
        console.error('❌ Checkout creation failed:', error);
        alert('Something went wrong creating checkout. Please try again.');
        setSubmitButtonDisabled(false);
      }

    } else {
      navigate("/choose-plan");
    }
  };

  const getDisplayUrl = (img) => {
    if (img.croppedSketchUrl) {
      return img.croppedSketchUrl;
    }
    return img.sketchUrl;
  };

  const renderDraggableGrid = () => {
    const coverImage = images.find(img => img.isCover);
    const sortableImages = images.filter(img => !img.isCover);

    return (
      <div className="flex flex-wrap items-start justify-center gap-2">
        {coverImage && (
          <div className="relative">
            <div className="group">
              <div className="border rounded-xl w-full shadow-sm bg-white overflow-hidden">
                <div className="aspect-[8/11] w-24 md:w-28 overflow-hidden rounded-xl">
                  <Image
                    src={getDisplayUrl(coverImage)}
                    alt="Cover Page"
                    classes="w-full h-full object-contain"
                  />
                </div>
              </div>
              <div className="relative text-center text-sm py-1 text-gray-400 font-light">
                Cover Page
              </div>
            </div>
          </div>
        )}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sortableItems}>
            {sortableImages.map((img, idx) => (
              <DraggableImageItem
                key={img.id}
                image={{
                  ...img,
                  sketchUrl: getDisplayUrl(img),
                  isRegenerating: regeneratingImages.has(img.id) || uploadingImages.includes(img.id),
                }}
                index={idx + 1}
                selected={selectedForRegen.includes(img.id)}
                onToggleSelect={toggleRegenSelect}
                onCrop={async () => {
                  const displayUrl = getDisplayUrl(img);
                  const base64 = await fetchImageAsBase64(displayUrl);
                  setCroppingImage({
                    ...img,
                    sketchUrl: base64,
                    cropData: img.cropData,
                  });
                }}
                disableSelection={img.isCover}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    );
  };

  const stepNumber = getActiveStep();

  const userId = session?.user?._id;
  const role = session?.user?.role;

  // 🆕 NEW: Socket listener for lineart progress + webhookSuccess
  useEffect(() => {
    let socket;

    if (userId && role === "user") {
      const token = localStorage.getItem('auth_token') || session?.user?.accessToken;

      if (!token) {
        console.error('No token found for socket authentication');
        return;
      }

      socket = io(SOCKET_URL, {
        auth: { token },
      });

      socket.on('connect', () => {
        console.log('🔌 Socket connected in Review:', socket.id);
      });

      // 🆕 Listen to lineart progress events
      socket.on('runpod:lineart:started', (data) => {
        console.log('🚀 Lineart started in Review:', data);
        if (data.bookId === activeBookId) {
          setLineartProgress({
            isProcessing: true,
            currentImage: 0,
            totalImages: data.totalImages || 0,
            overallPercentage: 0,
            currentStage: 'downloading',
          });
        }
      });

      socket.on('runpod:lineart:progress', (data) => {
        console.log('📊 Lineart progress in Review:', data);
        if (data.bookId === activeBookId) {
          setLineartProgress({
            isProcessing: true,
            currentImage: data.currentImage || 0,
            totalImages: data.totalImages || 0,
            overallPercentage: data.overallPercentage || 0,
            currentStage: data.currentStage || 'processing',
          });
        }
      });

      socket.on('runpod:lineart:completed', (data) => {
        console.log('✅ Lineart completed in Review:', data);
        if (data.bookId === activeBookId) {
          // Clear progress after a delay
          setTimeout(() => {
            setLineartProgress({
              isProcessing: false,
              currentImage: 0,
              totalImages: 0,
              overallPercentage: 0,
              currentStage: '',
            });
          }, 2000);
        }
      });

      socket.on('runpod:lineart:failed', (data) => {
        console.error('❌ Lineart failed in Review:', data);
        if (data.bookId === activeBookId) {
          setLineartProgress({
            isProcessing: false,
            currentImage: 0,
            totalImages: 0,
            overallPercentage: 0,
            currentStage: '',
          });
        }
      });

      socket.on('runpod:lineart:error', (data) => {
        console.error('❌ Lineart error in Review:', data);
        if (data.bookId === activeBookId) {
          setLineartProgress({
            isProcessing: false,
            currentImage: 0,
            totalImages: 0,
            overallPercentage: 0,
            currentStage: '',
          });
        }
      });

      socket.on('runpod:lineart:timeout', (data) => {
        console.error('⏱️ Lineart timeout in Review:', data);
        if (data.bookId === activeBookId) {
          setLineartProgress({
            isProcessing: false,
            currentImage: 0,
            totalImages: 0,
            overallPercentage: 0,
            currentStage: '',
          });
        }
      });

      // Webhook success listener
      socket.on('webhookSuccess', (data) => {
        console.log('✅ WEBHOOK SUCCESS RECEIVED:', data);
        if (data.bookId === activeBookId) {
          const allImages = [...coverImages, ...sketchImages].filter(img => !!img.AIUrl).map(img => img.AIUrl)

          dispatch(resetStep());
          dispatch(setCoverImages([]));
          dispatch(setReduxImages([]));
          dispatch(updateBookOrderDate({ bookId: activeBookId }));

          localStorage.removeItem('selectedPlan');
          navigate("/thank-you", {
            state: { 
              orderId: data.orderDetails.orderId, 
              bookId: activeBookId,
              imageUrls: allImages,
              paymentIntent: { 
                ...data.orderDetails.payment, 
                total: data.orderDetails.total 
              },
            } 
          });
        }
      });

      socket.on('disconnect', () => {
        console.log('🔌 Socket disconnected from Review');
      });

      socket.on('connect_error', (error) => {
        console.error('❌ Socket connection error:', error.message);
      });
    }

    return () => {
      if (socket) {
        socket.off('runpod:lineart:started');
        socket.off('runpod:lineart:progress');
        socket.off('runpod:lineart:completed');
        socket.off('runpod:lineart:failed');
        socket.off('runpod:lineart:error');
        socket.off('runpod:lineart:timeout');
        socket.off('webhookSuccess');
        socket.disconnect();
        console.log('🧹 Socket cleanup in Review');
      }
    };
  }, [userId, role, session?.token, activeBookId, coverImages, sketchImages, dispatch, navigate]);
  
  return (
    <div className="relative ">
        <h1 className="text-2xl md:text-3xl xl:text-4xl font-bold text-black mb-2 md:mb-4 xl:mb-6 text-center flex gap-5 items-center justify-center flex-col-reverse md:flex-row">
          Preview Your Book 
          <div className="h-6 md:h-9 flex items-center justify-center md:px-5 px-3 md:rounded-lg rounded-full border border-md-pink text-md-pink text-[10px] md:text-base">
            Step {stepNumber} of 5
          </div>
        </h1>
        <StepProgressView number={stepNumber}/>
        
        <div className="bg-transparent md:bg-white rounded-lg overflow-hidden py-3 px-2 md:px-6 flex items-center justify-between gap-5 lg:gap-4 mb-6 w-full flex-wrap">
          <div className="flex-shrink md:flex-shrink-0">
              <Button
                buttonLabel="Back"
                buttonClasses={"!rounded-lg !h-10 !px-11 md:!px-8 !gap-0.5 !text-sm"}
                 buttonLabelClasses={"text-sm !font-bold"}
                buttonFunction={async () => {
                  saveStep({
                    userId: session?.user?._id,
                    bookId: activeBookId,
                    isCompleted: 'yes',
                    stepCode: "Design_Your_Cover_Page",
                  });
                  dispatch(markStepAsVisited({ id: activeBookId, currentStepCode: 'Design_Your_Cover_Page' }))
                  navigate(`/cover-image?stepCode=${"Design_Your_Cover_Page"}`);
                }}
              />
          </div>
            
          <div className="flex-grow flex-shrink-0 md:flex-shrink  max-w-md order-last md:order-none">
              <div className="flex  items-start md:items-center justify-center gap-1 py-2 md:py-3 px-2 md:px-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg border border-pink-600 text-black font-bold">
                <div className="flex items-center gap-1 md:gap-2">
                  <i className="fa-solid fa-arrows-up-down-left-right text-base md:text-lg"></i>
                  <span className="text-sm lg:text-base">Drag to reorder pages.</span>
                </div>
              </div>
            </div>
          
          {selectedForRegen.length > 0 && !isRegenerating && (
            <div className="flex-shrink md:flex-shrink-0">
              <Button 
                buttonLabel={`${selectedForRegen.length}`} 
                buttonIcon="fa-solid fa-rotate" 
                buttonClasses={"!rounded-lg !h-10 !px-11 md:!px-8 !gap-0.5 !text-sm"}
                buttonLabelClasses={"text-sm !font-bold"}
                buttonFunction={handleRegenerate} 
              />
              </div>
          )}
          
          <div className="flex-shrink md:flex-shrink-0">
            <Button 
              buttonLabel="Finalize & Submit" 
              buttonClasses={"!rounded-lg !h-10 !px-2 md:!px-8 !gap-0.5 !text-sm"}
              buttonLabelClasses={" !text-xs md:!text-sm !font-bold"}
              isDisable={submitButtonDisabled}
              buttonFunction={handleFinalize} 
            />
          </div>
        </div>

        {/* 🔧 FIXED: Show progress when lineart is processing */}
        {lineartProgress.isProcessing && (
          <UploadProgressNotification progressData={lineartProgress} />
        )}

      {/* Main Content */}
      {isLoadingBatchImages ? (
        <div className="flex flex-col justify-center items-center h-96 bg-gradient-to-br from-pink-50 to-purple-50 rounded-3xl border border-pink-100">
          <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center mb-4 animate-pulse">
            <i className="fa-solid fa-spinner fa-spin text-white text-2xl"></i>
          </div>
          <p className="text-gray-700 text-lg font-semibold animate-pulse">Loading your coloring book...</p>
          <p className="text-gray-500 text-sm mt-2">Please wait while we prepare your pages</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg overflow-hidden py-3 px-2 md:px-6 flex items-center justify-between gap-2 lg:gap-4 mb-6 w-full">
            {renderDraggableGrid()}
        </div>
      )}

      {/* Cropper Modal */}
      {croppingImage && (
        <CropperModal
          isOpen={!!croppingImage}
          setIsOpen={() => setCroppingImage(null)}
          imageUrl={croppingImage.sketchUrl}
          originalUrl={croppingImage.originalUrl}
          cropData={croppingImage.cropData}
          onCrop={onCropSave}
          aspectRatio={8 / 11}
        />
      )}
    </div>
  );
};

export default ReviewBookContent;